import * as React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import SearchBar from '@coursera/coursera-ui/lib/components/extended/TableV2/ToolbarTable/SearchBar';

import type { Props as InnerProps } from 'bundles/enterprise-admin-drawers/components/components/TabbedFilterOptionList';

import _t from 'i18n!nls/admin-dashboard';

import type { FilterCtrl, FilterOption } from '../types';

type InjectedProps = {
  filterOptions: FilterOption[];
  loading: boolean;
};

type ConsumedProps = {
  searchQuery: string;
  filterType: string;
  organizationId: string;
  skipEmpty: boolean;
  preSorted?: boolean;
};

type OuterProps = Omit<InnerProps, keyof InjectedProps> & ConsumedProps;

export type FilterOptionContainerProps = OuterProps;

export type Props = {
  filterCtrl: FilterCtrl;
  selectedFilterOptions: FilterOption[];
  onChange: (selected: FilterOption[]) => void;

  //
  // include a select all/none option which was designed for the gradebook export.
  // optional to allow existing filter uses to simply ignore this new feature.
  //
  showSelectAllOption?: boolean;
  className?: string;
};

type State = {
  searchText: string;

  // debounced search text.
  searchQuery: string;
};

type Context = {
  ListComponentContainer: React.ComponentClass<FilterOptionContainerProps>;
  organizationId: string;
};

export default class FilterSelectorBody extends React.Component<Props, State> {
  state: State = {
    searchText: '',
    searchQuery: '',
  };

  static contextTypes = {
    ListComponentContainer: PropTypes.func,
    organizationId: PropTypes.string,
  };

  declare context: Context;

  componentDidUpdate(prevProps: Props) {
    const { filterCtrl } = this.props;

    // when switching to other filter selector, clear the text
    if (prevProps.filterCtrl !== filterCtrl) {
      this.setState({ searchText: '', searchQuery: '' });
    }
  }

  handleSearchTextChange = (keyword: string) => {
    this.setState({ searchText: keyword });

    // handle search
    this.setSearchQuery(keyword);
  };

  handleFilterChange = (filterOption: FilterOption, checked: boolean) => {
    const { selectedFilterOptions, onChange } = this.props;
    if (checked) {
      onChange([...selectedFilterOptions, filterOption]);
    } else {
      onChange(selectedFilterOptions.filter((fo) => fo.id !== filterOption.id));
    }
  };

  /**
   * Accept a new list of FilterOptions when the Select All option is used.
   */
  handleSelectAllChange = (nextValue: FilterOption[]) => {
    const { onChange } = this.props;

    onChange(nextValue);
  };

  setSearchQuery = _.debounce((query: string) => {
    const { filterCtrl } = this.props;
    this.setState({ searchQuery: query.length >= filterCtrl.minSearchLength ? query : '' });
  }, 500);

  render() {
    const { filterCtrl, selectedFilterOptions, className, showSelectAllOption } = this.props;
    const { searchText, searchQuery } = this.state;
    const { ListComponentContainer, organizationId } = this.context;

    const searchPlaceholder = _t('Search #{title}', { title: filterCtrl.title.toLocaleLowerCase() });

    return (
      <div className={`rc-FilterSelectorBody vertical-box ${className}`}>
        <h3 className="m-b-2">{filterCtrl.title}</h3>

        {filterCtrl.hideSearch ? null : (
          <div className="filterSearchInputWrapper">
            <SearchBar placeholder={searchPlaceholder} value={searchText} onUpdate={this.handleSearchTextChange} />
          </div>
        )}

        <ListComponentContainer
          className="flex-1 mh-0"
          searchQuery={searchQuery}
          filterType={filterCtrl.type}
          selectedFilterOptions={selectedFilterOptions}
          onChange={this.handleFilterChange}
          onChangeSelectAll={showSelectAllOption ? this.handleSelectAllChange : undefined}
          skipEmpty={filterCtrl.skipEmpty}
          preSorted={filterCtrl.preSorted}
          organizationId={organizationId}
        />
      </div>
    );
  }
}
